import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin, Phone } from "lucide-react";
import { useEffect } from "react";
import { PageHeader } from "@/components/page-header";
import { PageLoading } from "@/components/page-loading";
import { PageShell } from "@/components/page-shell";
import { Button } from "@/components/ui/button";
import { queryClient, repQueryOptions } from "@/lib/data";
import { retryIfCancelled } from "@/lib/query-client";
import { setUserReferral } from "@/lib/referral";
import { isSignedIn } from "@/lib/session";
import { useOrganization } from "@/lib/use-organization";

export const Route = createFileRoute("/reps/$id")({
  component: RepPage,
  loader: ({ params }) => {
    const options = repQueryOptions(params.id);
    return retryIfCancelled(options.queryKey, () =>
      queryClient.ensureQueryData(options),
    );
  },
});

function directionsUrl(address: string): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
}

function RepPage() {
  const { id } = Route.useParams();
  const organization = useOrganization();
  const { data: rep, isLoading } = useQuery(repQueryOptions(id));

  useEffect(() => {
    if (!isSignedIn() && rep?.slug) {
      setUserReferral(rep.slug);
    }
  }, [rep?.slug]);

  if (isLoading) {
    return <PageLoading />;
  }

  if (!rep) {
    return (
      <PageShell>
        <PageHeader title="Rep not found" backTo={null} />
        <p className="text-sm text-muted-foreground">
          We couldn't find that rep.
        </p>
      </PageShell>
    );
  }

  const authed = isSignedIn();
  const salesZone = rep.sales_zone;

  return (
    <PageShell hasStickyFooter>
      <PageHeader title={rep.name} backTo={authed ? "/steps" : null} />

      <div className="flex gap-4">
        {rep.headshot && (
          <img
            src={rep.headshot}
            alt={rep.name}
            className="size-24 shrink-0 rounded-xl border border-border object-cover shadow-[var(--shadow-card)]"
          />
        )}
        <div className="min-w-0 flex-1 space-y-3">
          {rep.phone_number && (
            <a
              href={`tel:${rep.phone_number}`}
              className="flex items-start gap-2 text-sm text-foreground hover:text-primary"
            >
              <Phone className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <span>{rep.phone_number}</span>
            </a>
          )}
          {salesZone?.physical_address && (
            <a
              href={directionsUrl(salesZone.physical_address)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-2 text-sm text-foreground hover:text-primary"
            >
              <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <span>{salesZone.physical_address}</span>
            </a>
          )}
        </div>
      </div>

      {organization.landing_page_content && (
        <div
          className="prose prose-sm mt-6 rounded-xl border border-border bg-card p-4 text-muted-foreground shadow-[var(--shadow-card)]"
          // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- Trusted content from organization settings
          dangerouslySetInnerHTML={{
            __html: organization.landing_page_content,
          }}
        />
      )}

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-background/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto w-full max-w-xl">
          <Button asChild size="lg" className="w-full">
            <Link to={authed ? "/steps" : "/"}>
              {authed ? "Return to your steps" : "Get started"}
            </Link>
          </Button>
        </div>
      </div>
    </PageShell>
  );
}
